import React, { useCallback, useEffect, useState } from 'react';
import {
  paymentApi,
  type LegacyReceivableDebtCleanupPreviewResult,
} from '../../api/paymentApi';
import toast from 'react-hot-toast';

interface ReceivableDebtCleanupModalProps {
  isOpen: boolean;
  buildingId: string;
  buildingName: string;
  onClose: () => void;
  onCleared: () => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('vi-VN').format(Math.round(value));

export const ReceivableDebtCleanupModal: React.FC<ReceivableDebtCleanupModalProps> = ({
  isOpen,
  buildingId,
  buildingName,
  onClose,
  onCleared,
}) => {
  const [preview, setPreview] = useState<LegacyReceivableDebtCleanupPreviewResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);

  const loadPreview = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const data = await paymentApi.previewLegacyReceivableDebtImportCleanup(buildingId);
      setPreview(data);
    } catch (error: unknown) {
      console.error(error);
      toast.error('Không tải được xem trước dữ liệu cũ');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [buildingId, onClose]);

  useEffect(() => {
    if (isOpen) {
      loadPreview();
    } else {
      setPreview(null);
    }
  }, [isOpen, loadPreview]);

  const handleClear = async () => {
    if (!preview || preview.deletableInvoiceCount === 0) return;
    const confirmed = window.confirm(
      `Xóa ${preview.deletableInvoiceCount} hóa đơn nhập công nợ cũ (tab Công nợ) cho ${buildingName}?`,
    );
    if (!confirmed) return;

    setClearing(true);
    try {
      const result = await paymentApi.clearLegacyReceivableDebtImport(buildingId);
      toast.success(
        `Đã xóa ${result.deletedInvoiceCount} hóa đơn` +
          (result.resetSurplusAccountCount > 0
            ? `, reset ${result.resetSurplusAccountCount} tài khoản thu dư`
            : '') +
          (result.skippedPaymentLinkedCount > 0
            ? ` (${result.skippedPaymentLinkedCount} hóa đơn có phiếu thu — giữ lại)`
            : ''),
      );
      onCleared();
      onClose();
    } catch (error: unknown) {
      console.error(error);
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(message || 'Không xóa được dữ liệu cũ');
    } finally {
      setClearing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Xóa nhập công nợ cũ (tab Công nợ)</h2>
          <p className="text-sm text-gray-500 mt-1">{buildingName}</p>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="py-12 text-center text-gray-500">Đang tải xem trước...</div>
          ) : preview ? (
            <>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                Xóa hóa đơn <strong>DV001</strong> được nhập qua tab Công nợ (ghi chú{' '}
                <em>Nhập công nợ kỳ … từ hệ thống cũ</em>) và reset thu dư migration. Sau đó nhập
                lại qua <strong>Chi tiết phí → CNNBG</strong> với kỳ <strong>09/2026</strong>.
              </p>

              {preview.warnings.length > 0 && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-1">
                  {preview.warnings.map((warning) => (
                    <p key={warning}>• {warning}</p>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">Hóa đơn cũ</div>
                  <div className="text-lg font-semibold">{preview.invoiceCount}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">Sẽ xóa</div>
                  <div className="text-lg font-semibold text-red-600">
                    {preview.deletableInvoiceCount}
                  </div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">Còn nợ (tổng)</div>
                  <div className="text-lg font-semibold">
                    {formatCurrency(preview.summary.remainingDebt)}
                  </div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">Reset thu dư</div>
                  <div className="text-lg font-semibold">
                    {preview.surplusAccountsToReset} (
                    {formatCurrency(preview.surplusBalanceTotal)})
                  </div>
                </div>
              </div>

              {preview.billingPeriods.length > 0 && (
                <p className="text-sm text-gray-600 mb-3">
                  Kỳ: {preview.billingPeriods.join(', ')} · {preview.apartmentCount} căn hộ
                </p>
              )}

              {preview.items.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Căn hộ</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Kỳ</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">Còn nợ</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Trạng thái</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {preview.items.map((item) => (
                        <tr key={`${item.apartmentCode}-${item.billingPeriod}`}>
                          <td className="px-3 py-2">{item.apartmentCode}</td>
                          <td className="px-3 py-2">{item.billingPeriod}</td>
                          <td className="px-3 py-2 text-right">
                            {formatCurrency(item.remainingDebt)}
                          </td>
                          <td className="px-3 py-2">
                            {item.blockedByPayment ? (
                              <span className="text-amber-700">Có phiếu thu — giữ</span>
                            ) : (
                              <span className="text-red-600">Sẽ xóa</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.invoiceCount > preview.items.length && (
                    <p className="px-3 py-2 text-xs text-gray-500 border-t border-gray-100">
                      Hiển thị {preview.items.length}/{preview.invoiceCount} dòng đầu
                    </p>
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            disabled={clearing}
          >
            Đóng
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={
              loading || clearing || !preview || preview.deletableInvoiceCount === 0
            }
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
          >
            {clearing ? 'Đang xóa...' : 'Xóa dữ liệu cũ'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceivableDebtCleanupModal;
